import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Workspace, Package } from '@/types/workspace';
import {
  getCurrentRootGroup,
  transformInvitedUsers,
  transformWorkspaces,
  transformWorspaceUsers,
} from './utils';

export interface WorkspaceState {
  workspaceUsers: ReturnType<typeof transformWorspaceUsers>;
  invitedUsers: ReturnType<typeof transformInvitedUsers>;
  currentUserGroup: string | null;
  allWorkspaces: Workspace[];
  createdGroup: Workspace | null;
  packages: Package[] | null;
  selectedPackage: Package | null;
  rootGroup: Workspace | undefined;
}

const initialState: WorkspaceState = {
  workspaceUsers: [],
  invitedUsers: [],
  currentUserGroup: null,
  allWorkspaces: [],
  createdGroup: null,
  packages: null,
  selectedPackage: null,
  rootGroup: undefined,
};

export const workspaceSlice = createSlice({
  name: 'workspace',
  initialState,
  reducers: {
    updateWorkspaceUsers(state, action: PayloadAction<{ Items?: unknown[]; invitedUsers?: unknown[] }>) {
      state.workspaceUsers = transformWorspaceUsers((action.payload?.Items || []) as Parameters<typeof transformWorspaceUsers>[0]);
      state.invitedUsers = transformInvitedUsers((action.payload?.invitedUsers || []) as Parameters<typeof transformInvitedUsers>[0]);
    },
    updateCurrentUserGroup(state, action: PayloadAction<string | null>) {
      state.currentUserGroup = action.payload;
      state.rootGroup = getCurrentRootGroup(state.allWorkspaces, action.payload);
    },
    setCreatedGroup(state, action: PayloadAction<Workspace | null>) {
      state.createdGroup = action.payload;
    },
    setAllWorkspaces(state, action: PayloadAction<{ items?: unknown[]; uid?: string; packages?: Package[] | null; selectedPackage?: Package | null }>) {
      const workspaces = transformWorkspaces((action.payload?.items || []) as Parameters<typeof transformWorkspaces>[0]);
      state.allWorkspaces = workspaces;

      if (action.payload.uid) {
        const rootGroups = workspaces.filter((i) => i.parent === 'root');
        state.createdGroup = rootGroups.find((i) => i.creator === action.payload.uid) ?? null;
        state.packages = action.payload.packages ?? null;
        state.selectedPackage = action.payload.selectedPackage ?? null;
      }

      const localStorageCurrentUserGroup = window.localStorage.getItem('currentUserGroup');
      let currentUserGroup: string | null = null;
      if (
        localStorageCurrentUserGroup &&
        workspaces.some((i) => i.id === localStorageCurrentUserGroup)
      ) {
        currentUserGroup = localStorageCurrentUserGroup;
      } else {
        currentUserGroup = workspaces[0]?.id ?? null;
      }
      state.currentUserGroup = currentUserGroup;
      state.rootGroup = getCurrentRootGroup(workspaces, currentUserGroup);
    },
  },
});

export const { updateWorkspaceUsers, updateCurrentUserGroup, setAllWorkspaces, setCreatedGroup } =
  workspaceSlice.actions;

export default workspaceSlice.reducer;
